import { ResearchPlotData } from '@resistapp/client/data-utils/plot-data/research-plot-data';
import { L2Target, L2Targets } from '@resistapp/common/assays';
import { friendlyL2Target } from '@resistapp/common/friendly';
import { SampleSubType, SampleType, friendlySampleSubTypeOrType } from '@resistapp/common/sampling-point-types';
import { NormalisationMode } from '@resistapp/common/types';
import { Line } from '@visx/shape';
import { useResearchContext } from '../../contexts/research-context';

interface ReferenceLineProps {
  normalizationMode?: NormalisationMode;
  yScale: (n: number) => number;
  width: number;
  left: number;
  stroke?: string;
  strokeWidth?: number;
  strokeDasharray?: string;
  opacity?: number;
}

/**
 * Renders a reference line on a chart
 * For the research view, this represents a global reference level
 */
export function BoxPlotMeanLine({
  yScale,
  width,
  left,
  stroke = '#666666',
  strokeWidth = 1.5,
  strokeDasharray = '5,5',
  opacity = 0.7,
}: ReferenceLineProps) {
  const { getReferenceLevel } = useResearchContext();
  const [referenceValue] = getReferenceLevel();

  if (referenceValue === null) {
    return null;
  }

  const y = yScale(Math.log10(referenceValue));

  return (
    <Line
      className="download_reference-line"
      from={{ x: left, y }}
      to={{ x: left + width, y }}
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeDasharray={strokeDasharray}
      opacity={opacity}
    />
  );
}

type ReferenceLevelTooltipContentProps = {
  plotData: ResearchPlotData | null;
  selectedTargets: L2Target[];
  environmentTypeOrSubtype: SampleType | SampleSubType | null;
};

/**
 * Generates the tooltip content for the reference level line
 */
export function ReferenceLevelTooltipContent(props: ReferenceLevelTooltipContentProps): JSX.Element | null {
  const { plotData, selectedTargets, environmentTypeOrSubtype } = props;
  if (!plotData) return null;

  const type = (environmentTypeOrSubtype && friendlySampleSubTypeOrType(environmentTypeOrSubtype).toLowerCase()) || '';
  const { normalisationMode } = plotData;
  const metric =
    normalisationMode === NormalisationMode.LITRE_16S
      ? 'copy number per L (16S std. curve)'
      : normalisationMode === NormalisationMode.TEN_UL_DILUTED_DNA_16S
        ? 'copies per 10 uL DNA (16S std. curve)'
        : normalisationMode === NormalisationMode.LITRE_AY
          ? 'copy number per L (ay std. curve)'
          : normalisationMode === NormalisationMode.TEN_UL_DILUTED_DNA_AY
            ? 'copies per 10 uL DNA (ay std. curve)'
            : 'relative abundance';

  const targetsPhrase =
    selectedTargets.length === 1
      ? `${selectedTargets[0].length > 4 ? friendlyL2Target(selectedTargets[0] as L2Targets).toLowerCase() : friendlyL2Target(selectedTargets[0] as L2Targets)} genes`
      : 'gene classes';

  return (
    <>
      <p style={{ marginBottom: '1rem' }}>
        <b>Mean {metric}</b> for the <b>selected {targetsPhrase}</b> in {type} across the whole Resistomap database.
      </p>
    </>
  );
}
